import {
  Box,
  Stack,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputAdornment,
  Button,
} from "@mui/material";
import {
  DataGrid,
  GridRenderCellParams,
  GridRowParams,
  useGridApiRef,
} from "@mui/x-data-grid";
import TransactionIcon from "assets/svg/TransactionIcon";
import AdminPageCardTitle from "components/AdminPageCardTitle/AdminPageCardTitle";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FETCH_ADMIN_TRANSACTIONS } from "reduxStore/actions/authActionType";
import { RootState } from "reduxStore/store";
import { transactionColumn } from "./dataGridDef";
import SearchIcon from "assets/svg/SearchIcon";
import NoRowsTableDataOverlay from "components/NoRowsTableDataOverlay/NoRowsTableDataOverlay";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

const statusOptions = ["Completed", "Pending", "Failed"];
const typeOptions = ["Transfer", "Receive"];

const TransactionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const { adminTransactions, loading } = useSelector(
    (state: RootState) => state.adminTransaction
  );

  const [filters, setFilters] = useState({
    search_text: "",
    status: "",
    type: "",
    from_date: null as Dayjs | null,
    to_date: null as Dayjs | null,
  });

  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<any[]>([]);

  useEffect(() => {
    // Fetch all transactions once (large limit, adjust as needed)
    dispatch({
      type: FETCH_ADMIN_TRANSACTIONS,
      payload: { page: 1, limit: 1000 },
    });
  }, [dispatch]);

  useEffect(() => {
    setAllTransactions(adminTransactions?.data?.transactions || []);
  }, [adminTransactions]);

  useEffect(() => {
    let txs = allTransactions;
    // Search by user name or tx hash
    if (filters.search_text) {
      const searchLower = filters.search_text.toLowerCase();
      txs = txs.filter((tx: any) => {
        const name = `${tx.firstName || ""} ${tx.lastName || ""}`.toLowerCase();
        const txHash = (tx.txHash || "").toLowerCase();
        return name.includes(searchLower) || txHash.includes(searchLower);
      });
    }
    // Status filter
    if (filters.status) {
      txs = txs.filter(
        (tx: any) =>
          (tx.status || "").toLowerCase() === filters.status.toLowerCase()
      );
    }
    // Type filter
    if (filters.type) {
      txs = txs.filter(
        (tx: any) =>
          (tx.txType || "").toLowerCase() === filters.type.toLowerCase()
      );
    }
    // Date range filter
    if (filters.from_date) {
      txs = txs.filter((tx: any) => {
        if (!tx.createdAt || !filters.from_date) return false;
        return dayjs(tx.createdAt).isAfter(
          filters.from_date.subtract(1, "day")
        );
      });
    }
    if (filters.to_date) {
      txs = txs.filter((tx: any) => {
        if (!tx.createdAt || !filters.to_date) return false;
        return dayjs(tx.createdAt).isBefore(filters.to_date.add(1, "day"));
      });
    }
    setFilteredTransactions(txs);
  }, [filters, allTransactions]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const handleFilterChange = (field: string, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page on filter change
  };

  const handleClearAll = () => {
    setFilters({
      search_text: "",
      status: "",
      type: "",
      from_date: null,
      to_date: null,
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/admin/transactions/${params.row.transactionId}`);
  };

  const updatedColumns = transactionColumn.map((col) =>
    col.field === "id"
      ? {
          ...col,
          renderCell: (params: GridRenderCellParams) => {
            const { id } = params;
            const rowIndex = params.api.getRowIndexRelativeToVisibleRows(id);
            const page =
              apiRef?.current?.state?.pagination?.paginationModel?.page;
            const pageSize =
              apiRef?.current?.state?.pagination?.paginationModel?.pageSize ||
              10;
            return rowIndex + 1 + page * pageSize;
          },
        }
      : col
  );

  return (
    <Box sx={styles.container}>
      <AdminPageCardTitle
        title={"Transactions"}
        icon={<TransactionIcon width={28} height={28} />}
      />
      {/* Filter Bar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          justifyContent: "flex-end",
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        {/* Search Box */}
        <TextField
          placeholder="Search by User Name"
          value={filters.search_text}
          onChange={(e) => handleFilterChange("search_text", e.target.value)}
          size="small"
          sx={{
            maxWidth: 220,
            mb: { xs: 2, sm: 0 },
            "& .MuiInputBase-input": { fontSize: 13 },
            "& .MuiInputLabel-root": { fontSize: 13 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon width={20} height={20} />
              </InputAdornment>
            ),
          }}
        />
        {/* Status Dropdown */}
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            displayEmpty
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            sx={{ color: filters.status ? "inherit" : "#b0b0b0", fontSize: 13 }}
          >
            <MenuItem
              value=""
              disabled
              style={{ color: "#b0b0b0", fontSize: 13 }}
            >
              Status
            </MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status} sx={{ fontSize: 13 }}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Type Dropdown */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            displayEmpty
            value={filters.type}
            onChange={(e) => handleFilterChange("type", e.target.value)}
            sx={{ color: filters.type ? "inherit" : "#b0b0b0", fontSize: 13 }}
          >
            <MenuItem
              value=""
              disabled
              style={{ color: "#b0b0b0", fontSize: 13 }}
            >
              Transaction Type
            </MenuItem>
            {typeOptions.map((type) => (
              <MenuItem key={type} value={type} sx={{ fontSize: 13 }}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="From Date"
            value={filters.from_date}
            onChange={(newValue) => handleFilterChange("from_date", newValue)}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  maxWidth: 150,
                  "& .MuiInputBase-input": { fontSize: 13 },
                },
                InputLabelProps: { shrink: true, sx: { fontSize: 13 } },
                placeholder: "From Date",
              },
            }}
            maxDate={filters.to_date || undefined}
          />
          <DatePicker
            label="To Date"
            value={filters.to_date}
            onChange={(newValue) => handleFilterChange("to_date", newValue)}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  maxWidth: 150,
                  "& .MuiInputBase-input": { fontSize: 13 },
                },
                InputLabelProps: { shrink: true, sx: { fontSize: 13 } },
                placeholder: "To Date",
              },
            }}
            minDate={filters.from_date || undefined}
          />
        </LocalizationProvider>
        {/* Clear All Button */}
        <Box
          sx={{
            width: { xs: "100%", sm: "auto" },
            display: "flex",
            justifyContent: { xs: "flex-end", sm: "unset" },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearAll}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Clear All
          </Button>
        </Box>
      </Box>
      <DataGrid
        apiRef={apiRef}
        rows={filteredTransactions}
        columns={updatedColumns}
        getRowId={(row) => row.transactionId}
        onRowClick={handleRowClick}
        loading={loading}
        slots={{
          noRowsOverlay: () => (
            <NoRowsTableDataOverlay title={"No transaction data"} />
          ),
        }}
        sx={{
          border: 0,
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
        }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        rowCount={filteredTransactions.length}
        paginationMode="client"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10]}
        disableRowSelectionOnClick
      />
    </Box>
  );
};

export default TransactionPage;
const styles = {
  container: {
    flexGrow: 1,
    borderRadius: 3,
    boxShadow: `rgba(8, 60, 61, 0.27) 0px 1px 5px 0px, rgba(8, 60, 61, 0.06) 0px 1px 5px 0px`,
    backgroundColor: "rgb(255, 255, 255)",
    p: 3,
    ml: {
      xs: 0,
      md: -5,
    },
  },
};
